import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase';
import { useAuth } from '../../../../../hooks/useAuth';
import type { ApplicationData, DocumentInfo } from '../types';

interface UseApplicationDataReturn {
  application: ApplicationData | null;
  loading: boolean;
  error: string | null;
  documentUrls: {[key: string]: DocumentInfo};
  waitingPosition: number | null;
  setApplication: React.Dispatch<React.SetStateAction<ApplicationData | null>>;
  setDocumentUrls: React.Dispatch<React.SetStateAction<{[key: string]: DocumentInfo}>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useApplicationData = (): UseApplicationDataReturn => {
  const { user, loading: authLoading } = useAuth();
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentUrls, setDocumentUrls] = useState<{[key: string]: DocumentInfo}>({});
  const [waitingPosition, setWaitingPosition] = useState<number | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const getMillis = (value: any): number => {
      try {
        if (!value) return 0;
        if (typeof value.toMillis === 'function') return value.toMillis();
        if (typeof value.toDate === 'function') return value.toDate().getTime();
        if (value instanceof Date) return value.getTime();
        return new Date(value).getTime();
      } catch {
        return 0;
      }
    };

    const fetchApplicationData = async () => {
      // authLoading이 완료될 때까지 대기
      if (authLoading) {
        return;
      }

      // 인증 로딩이 완료된 후 user가 없으면 에러 설정
      if (!user) {
        if (isMounted) {
          setError('로그인이 필요합니다.');
          setLoading(false);
        }
        return;
      }

      try {
        // NOTE: 만료/포기 후 재신청 정책
        // - completed/terminated(종료) 이력이 있는 경우, 그 이전에 생성된 신청서는 "현재 회차"로 보지 않는다.
        // - 대기순번 계산 역시 "현재 회차"의 pending/checked만 기준으로 한다.
        let terminalAnchorMillis = 0;
        const residenciesRef = collection(db, 'portville_residencies');
        const applicationsRef = collection(db, 'portville_applications');
        const applicationsQuery = query(
          applicationsRef,
          where('uid', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(10)
        );

        const loadTerminalAnchorMillis = async (): Promise<number> => {
          try {
            // 1) Fast path: 종료 상태 중 최신 1건만 조회
            const terminalQuery = query(
              residenciesRef,
              where('residentUid', '==', user.uid),
              where('status', 'in', ['completed', 'terminated']),
              orderBy('updatedAt', 'desc'),
              limit(1)
            );

            const terminalSnapshot = await getDocs(terminalQuery);
            if (abortController.signal.aborted || terminalSnapshot.empty) return 0;

            const data = terminalSnapshot.docs[0].data() as any;
            return (
              getMillis(data?.completedAt) ||
              getMillis(data?.terminatedAt) ||
              getMillis(data?.contractCancelledAt) ||
              getMillis(data?.updatedAt) ||
              getMillis(data?.contractSignedAt) ||
              0
            );
          } catch (anchorErr) {
            // 2) Fallback: 최신 N건만 조회 후 상태 필터링 (인덱스/쿼리 제한 대비)
            try {
              const latestQuery = query(
                residenciesRef,
                where('residentUid', '==', user.uid),
                orderBy('updatedAt', 'desc'),
                limit(20)
              );
              const latestSnapshot = await getDocs(latestQuery);
              if (abortController.signal.aborted || latestSnapshot.empty) return 0;

              return latestSnapshot.docs.reduce((acc, snap) => {
                const data = snap.data() as any;
                const status = data?.status as string | undefined;
                if (!(status === 'completed' || status === 'terminated')) return acc;
                const key =
                  getMillis(data?.completedAt) ||
                  getMillis(data?.terminatedAt) ||
                  getMillis(data?.contractCancelledAt) ||
                  getMillis(data?.updatedAt) ||
                  getMillis(data?.contractSignedAt);
                return Math.max(acc, key);
              }, 0);
            } catch (fallbackErr) {
              // 3) Last resort: 전체 조회 (느릴 수 있음)
              try {
                const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
                const residencySnapshot = await getDocs(residencyQuery);
                if (abortController.signal.aborted || residencySnapshot.empty) return 0;

                return residencySnapshot.docs.reduce((acc, snap) => {
                  const data = snap.data() as any;
                  const status = data?.status as string | undefined;
                  if (!(status === 'completed' || status === 'terminated')) return acc;
                  const key =
                    getMillis(data?.completedAt) ||
                    getMillis(data?.terminatedAt) ||
                    getMillis(data?.contractCancelledAt) ||
                    getMillis(data?.updatedAt) ||
                    getMillis(data?.contractSignedAt);
                  return Math.max(acc, key);
                }, 0);
              } catch (lastErr) {
                // anchor 계산 실패 시에도 신청 내역 자체는 표시할 수 있으므로 계속 진행한다.
                console.warn('종료 이력(anchor) 계산 실패:', { anchorErr, fallbackErr, lastErr });
                return 0;
              }
            }
          }
        };

        const [anchorMillis, querySnapshot] = await Promise.all([
          loadTerminalAnchorMillis(),
          getDocs(applicationsQuery)
        ]);

        terminalAnchorMillis = anchorMillis;

        if (abortController.signal.aborted) return;

        if (querySnapshot.empty) {
          if (isMounted) {
            setError('신청 내역이 없습니다.');
            setLoading(false);
          }
          return;
        }

        const nonConsumedDocs = querySnapshot.docs.filter((d) => (d.data() as any)?.status !== 'consumed');
        const currentCycleDocs =
          terminalAnchorMillis > 0
            ? nonConsumedDocs.filter((d) => getMillis((d.data() as any)?.createdAt) > terminalAnchorMillis)
            : nonConsumedDocs;

        // NOTE: 최신순(createdAt desc)으로 조회되므로, find()는 "가장 최근"을 선택한다.
        const pickFirstByStatus = (docs: typeof currentCycleDocs, statuses: string[]) =>
          docs.find((d) => statuses.includes(((d.data() as any)?.status as string | undefined) ?? ''));

        const selectedDoc =
          pickFirstByStatus(currentCycleDocs, ['pending', 'checked', 'approved']) ??
          pickFirstByStatus(currentCycleDocs, ['rejected']) ??
          nonConsumedDocs[0] ??
          null;

        if (!selectedDoc) {
          if (isMounted) {
            setError('신청 내역이 없습니다.');
            setLoading(false);
          }
          return;
        }

        const doc = selectedDoc;
        const data = doc.data() as ApplicationData;

        if (isMounted) {
          setApplication({ ...data, id: doc.id });
          // NOTE: 제출서류는 storagePath 기반으로 즉시 렌더하고,
          // 다운로드 URL은 실제 다운로드 클릭 시점에만 조회한다.
          const urls: {[key: string]: DocumentInfo} = {};
          Object.entries(data.documents || {}).forEach(([docType, path]) => {
            if (!path) return;
            const fileName = path.split('/').pop() || '';
            const originalFileName = fileName.split('_').slice(3).join('_') || fileName;
            urls[docType] = {
              url: '',
              originalFileName,
              storagePath: path
            };
          });
          setDocumentUrls(urls);
          setLoading(false);
        }

        const createdAtMillis = getMillis((data as any)?.createdAt);
        const isCurrentCycle = terminalAnchorMillis === 0 || createdAtMillis > terminalAnchorMillis;
        const isWaitingStatus = data.status === 'pending' || data.status === 'checked';

        if (isWaitingStatus && isCurrentCycle && createdAtMillis > 0) {
          const computeWaitingPosition = async () => {
            const pendingQuery = query(
              collection(db, 'portville_applications'),
              where('status', 'in', ['pending', 'checked']),
              orderBy('createdAt', 'asc')
            );

            try {
              // 전체 pending/checked 대기열에서 현재 신청서 문서의 위치를 순번으로 사용한다.
              // 과거 consumed/rejected 이력이나 residency anchor가 현재 대기 순번을 잘라내지 않도록 한다.
              const snapshot = await getDocs(pendingQuery);

              if (abortController.signal.aborted) return;

              const index = snapshot.docs.findIndex((d) => d.id === doc.id);
              if (isMounted) {
                setWaitingPosition(index >= 0 ? index + 1 : null);
              }
            } catch (listErr) {
              console.warn('대기 순번 계산 실패:', listErr);
              if (isMounted) {
                setWaitingPosition(null);
              }
            }
          };

          void computeWaitingPosition();
        } else if (isMounted) {
          setWaitingPosition(null);
        }

      } catch (err) {
        console.error('신청 내역 조회 중 오류 발생:', err);
        if (isMounted) {
          setError('신청 내역을 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
        }
      }
    };

    fetchApplicationData();

    // Cleanup 함수: 컴포넌트 언마운트 시 비동기 작업 정리
    return () => {
      abortController.abort();
      isMounted = false;
    };
  }, [user, authLoading]);

  return {
    application,
    loading,
    error,
    documentUrls,
    waitingPosition,
    setApplication,
    setDocumentUrls,
    setError
  };
};
